/**
 * Unified multi-domain "My Bookings" dashboard (Hotels/Sports/Shows/Restaurants/Concerts merged
 * into one list). Reactivated as a real route (2026-08-26) after being disabled for one deploy
 * cycle (commit `bedff418`, folder renamed to `app/_manage-bookings-unused/`).
 *
 * This is the live `/manage-bookings` route on EVERY deploy, hotels included — the old
 * `hotelsDomain`-gated `/manage-bookings` → `/hotels/manage-bookings` rewrite in `next.config.js`
 * has been removed so it no longer shadows this page (`app/hotels/manage-bookings/page.tsx` is
 * kept as a dead route, reachable only by direct URL). ⚠️ On a CMS-fronted production/staging
 * environment, the CMS also owns a `/manage-bookings` page at the CDN edge on non-hotels deploys —
 * the two collide at the same path. This was the reason for the earlier disable. Restoring the
 * original path was a deliberate choice (rather than moving to a new, non-colliding path) —
 * coordinate with whoever owns the CloudFront/CMS edge routing before this reaches such an
 * environment.
 */
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  authClient,
  hotelsClient,
  restaurantsClient,
  sportsEventsClient,
  concertsEventsClient,
  showsClient,
  type BookingListItem,
  type RestaurantBookingListItem,
  type SportsBookingListItem,
  type ShowBooking,
  type ShowsBookingListItem,
} from '@bip/api-client';
import { formatEventDateTime, cleanCategoryName } from '@bip/shared';
import { SkeletonBox } from '../_components/Skeleton';
import { AccountShell } from '../hotels/_components/AccountShell';
import { LoginGate } from '../hotels/_components/LoginGate';
import { PROXY_PREFIX } from '../hotels/config';
import { resolveRestaurantImage, RESTAURANT_NO_IMAGE_URL } from '../restaurants/config';
import '../styles/manage-bookings.css';

const HOME_HREF = `${PROXY_PREFIX}/hotels`;

// CMS Manage Bookings categories (Manage Bookings.html:49): All Bookings / Hotels / Shows / Sports.
// "Restaurants" and "Concerts" added alongside — the CMS array never listed them, but this page is
// now the shared Restaurants/Concerts tab too (see plan Section 5 step 4). Order matches the
// bookingType numbering (Hotel=1, Shows=2, Sports=3, Restaurant=4, Concert=5) below.
const CATEGORIES = ['All Bookings', 'Hotels', 'Shows', 'Sports', 'Restaurants', 'Concerts'];
/** Numeric & string `bookingType` mapping carried on confirmation-page "View my Bookings" links
 *  (Hotel=1, Shows=2, Sports=3, Restaurant=4, Concert=5, plus string aliases) — used to pre-select this page's
 *  category dropdown so a confirmed booking lands on its own tab, not the "All Bookings" default. */
const BOOKING_TYPE_TO_CATEGORY: Record<string, string> = {
  '1': 'Hotels',
  'hotel': 'Hotels',
  'hotels': 'Hotels',
  '2': 'Shows',
  'shows': 'Shows',
  'show': 'Shows',
  'theatre': 'Shows',
  'theater': 'Shows',
  '3': 'Sports',
  'sports': 'Sports',
  'sport': 'Sports',
  '4': 'Restaurants',
  'restaurant': 'Restaurants',
  'restaurants': 'Restaurants',
  '5': 'Concerts',
  'concerts': 'Concerts',
  'concert': 'Concerts',
};

function resolveCategoryFromBookingType(bType?: string | null): string {
  if (!bType) return 'All Bookings';
  const clean = bType.trim().toLowerCase();
  return BOOKING_TYPE_TO_CATEGORY[clean] || BOOKING_TYPE_TO_CATEGORY[bType.trim()] || 'All Bookings';
}

/** Reverse of `BOOKING_TYPE_TO_CATEGORY`'s numeric keys — used to write `?bookingType=N` back to
 *  the URL when the user changes the category dropdown, so the page stays deep-linkable at any
 *  point, not just on the initial confirmation-page redirect. */
const CATEGORY_TO_BOOKING_TYPE: Record<string, string> = {
  Hotels: '1',
  Shows: '2',
  Sports: '3',
  Restaurants: '4',
  Concerts: '5',
};
const PAGE_SIZE = 15;
/** "All Bookings" fetches EVERY record from each source in one shot (CMS parity — the reference's
 *  `get-all-bookings` calls `sportsBookings()`/`hotelsBookings()`/`showsBookings()` with NO
 *  pagination at all, merges, sorts by `CreatedDate` globally, then slices the page window out of
 *  that one correctly-ordered array). Paginating each source independently (this page's original
 *  approach) breaks global date order across "Load more" scrolls — e.g. sports' page 2 can contain
 *  dates that belonged interleaved with hotels' page 1, not after it. A generous upper bound, not a
 *  real page size — large enough to cover a user's full booking history in one request per source. */
const ALL_BOOKINGS_FETCH_SIZE = 1000;
/** Merge-source timeout for the "All Bookings" tab — caps the worst-case wait on any ONE of the
 *  3 parallel sources (a slow/unreachable service, e.g. restaurants-service not running locally,
 *  otherwise blocks the whole tab switch until it times out on its own). */
const MERGE_SOURCE_TIMEOUT_MS = 3000;

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

/** Hotels items don't carry a `bookingType` discriminator of their own (`hotelsClient.getBookings`
 *  returns plain `BookingListItem`) — stamped on the way in wherever a hotel record enters state,
 *  same convention as Shows/Restaurants/Sports/Concerts (each already typed with their own literal
 *  `bookingType`). */
type HotelBookingItem = BookingListItem & { bookingType: 'hotel' };

type AnyBookingItem =
  | HotelBookingItem
  | RestaurantBookingListItem
  | SportsBookingListItem
  | ShowsBookingListItem;

function itemId(b: AnyBookingItem): string {
  if (b.bookingType === 'restaurant') return b._id;
  if (b.bookingType === 'shows') return b._id;
  return b.id;
}

/** Best-effort sort key for the "All Bookings" interleave (plan Section 4) — each domain's own
 *  "created" date field, parsed loosely (pre-formatted display strings parse fine for this purpose;
 *  an unparseable value sorts to the bottom rather than throwing). */
function dateKey(b: AnyBookingItem): number {
  const raw = b.bookingType === 'hotel' ? b.bookingDate : b.bookingType === 'shows' ? b.ConfirmedAt : b.CreatedDate;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
}

/** Exact port of `manage-booking-api.js` `showsBookings()`/`get-my-theater-bookings`:
 *  `moment(ConfirmedAt).format('DD MMMM YYYY')` — full month name (unlike hotels/sports' short
 *  `'DD MMM YYYY'`), matching the restaurant card's day/month/year shape but zero-padded day. */
function formatShowBookingDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  return `${day} ${month} ${d.getFullYear()}`;
}

/** Exact port of the same function's `VenueDetail`: `[VenueName, VenueAddress, VenueCity,
 *  VenuePostCode].filter(Boolean).join(", ")`, falling back to "Location Unavailable". Reads the
 *  FIRST basket item only (matches the CMS, which never merges venue fields across items). */
function showVenueDetail(item: ShowBooking): string {
  const venue = item.basketData?.Items?.[0];
  if (!venue?.VenueName) return 'Location Unavailable';
  return [venue.VenueName, venue.VenueAddress, venue.VenueCity, venue.VenuePostCode]
    .filter(Boolean)
    .join(', ');
}

/** Exact port of the same function's `tickets`/`Seats` pair: ticket count is summed across EVERY
 *  basket item (`Seats.length`, not just the first item), then seats are grouped by `AreaName`
 *  (`"{area} - {seat, seat}"`, joined with `" | "` across areas) — combined on the card as
 *  `${tickets} - ${Seats}` (`manage-booking.html`'s literal template). */
function showTicketsAndSeats(item: ShowBooking): { ticketsLabel: string; seatsLabel: string } | null {
  const items = item.basketData?.Items;
  if (!items?.length) return null;
  let totalSeats = 0;
  const seatsByArea: Record<string, string[]> = {};
  for (const el of items) {
    totalSeats += el.Seats?.length ?? 0;
    const area = el.AreaName || '';
    const seatsStr = (el.Seats || []).join(', ');
    (seatsByArea[area] ??= []).push(seatsStr);
  }
  if (!totalSeats) return null;
  const ticketsLabel = totalSeats === 1 ? '1 Ticket' : `${totalSeats} Tickets`;
  const seatsLabel = Object.entries(seatsByArea)
    .map(([area, seats]) => `${area} - ${seats.join(', ')}`)
    .join(' | ');
  return { ticketsLabel, seatsLabel };
}

/** Reads the backend's pre-computed `TotalPrice` (`my-bookings.service.ts` — built from the
 *  order's stringified `paymentInfo`, which is the field most real docs actually carry;
 *  `paymentDetails` is a newer/optional object absent on most production orders). Falls back to
 *  deriving from `paymentDetails` directly only if `TotalPrice` wasn't sent. */
function showTotalPrice(item: ShowBooking): string {
  if (item.TotalPrice) return item.TotalPrice;
  const p = item.paymentDetails;
  if (!p || p.converTedPrice == null) return '';
  return `${p.currency || '£'} ${p.converTedPrice.toFixed(2)}`;
}

/** 1:1 port of `manage-booking.html`'s Shows card (`col-md-2` image / `col-md-7` details /
 *  `col-md-3` price+status — matches the reference's bootstrap column split exactly, unlike the
 *  restaurant card's `col-md-2/8/2`). Whole card is the link, no separate "View Details" button.
 *  `statusHTML` is tab-driven, NOT record-driven (CMS: the Shows query only ever returns
 *  `Status: "BOOKING_CONFIRMED"` docs — a cancelled show simply never appears in this list — so
 *  "Confirmed" renders ONLY on the Active/Upcoming tab; the Other/Past tab shows no status label
 *  at all, matching the reference's `d.statusHTML` being `null` for the Past query). */
function ShowsBookingCard({
  booking,
  activeTab,
}: {
  booking: ShowsBookingListItem;
  activeTab: 'active' | 'other';
}) {
  const item = booking.basketData?.Items?.[0];
  const seats = showTicketsAndSeats(booking);
  const bookingDate = formatShowBookingDate(booking.ConfirmedAt);
  const price = showTotalPrice(booking);

  return (
    <Link href={`${PROXY_PREFIX}/manage-bookings/shows-booking-detail?cartId=${booking._id}`} className="block no-underline">
      <div className="p-6 rounded-lg mb-6 bg-white rebbet-shape">
        <div className="flex flex-col md:flex-row -mx-[15px]">
          <div className="md:w-1/6 px-[15px]">
            {item?.ImageUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={item.ImageUrl} alt={item.EventName} className="w-full rounded-lg" />
            )}
          </div>
          <div className="md:w-7/12 px-[15px]">
            <h4 className="font-display font-bold text-secondary leading-snug">{item?.EventName}</h4>
            <div className="text-[15px] text-black mb-2 md:mb-4">{showVenueDetail(booking)}</div>
            <div className="grid grid-cols-12 gap-x-[30px] pt-1">
              {booking.performanceDate && (
                <div className="col-span-7">
                  <div className="text-[15px] text-[#0065A1] mb-[2px]">Show Date &amp; Time</div>
                  <div className="text-[15px] text-black">{booking.performanceDate}</div>
                </div>
              )}
              {seats && (
                <div className="col-span-5">
                  <div className="text-[15px] text-[#0065A1] mb-[2px] block">Number of Seats</div>
                  <div className="text-[15px] text-black">
                    {seats.ticketsLabel} - {seats.seatsLabel}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-1">
              {booking.reservationId && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Confirmation Number: </span>
                  <span className="text-black">{booking.reservationId}</span>
                </div>
              )}
              {bookingDate && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Date: </span>
                  <span className="text-black">{bookingDate}</span>
                </div>
              )}
            </div>
          </div>
          <div className="md:w-1/4 px-[15px] md:text-right mt-4 md:mt-0 flex flex-col justify-between">
            {price && (
              <div className="text-[18px] md:text-[1.5rem] font-semibold text-black">{price}</div>
            )}
            {activeTab === 'active' && (
              <h5>
                <span className="text-green">Confirmed</span>
              </h5>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

/** `retDatObj.statusHTML` (`manage-booking-api.js` `hotelsBookings`/`get-booking-data-list`) is
 *  plain colored text in an `<h5>` — `clr-green`/`text-info`/`text-danger` — never a pill/badge. */
const HOTEL_STATUS_CLASS: Record<string, string> = {
  Confirmed: 'text-green',
  'In Progress': 'text-info',
  Cancelled: 'text-danger',
  'No show': 'text-danger',
};

/** Exact port of the restaurant `retDatObj.BookingDate`: `moment(CreatedDate).format('D MMMM YYYY')`
 *  — no leading zero on the day (unlike hotels/sports' `'DD MMM YYYY'`), full month name. */
function formatRestaurantBookingDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  return `${d.getDate()} ${month} ${d.getFullYear()}`;
}

/** 1:1 port of `dashboard-code/manage-booking.html`'s hotel card (`col-lg-3` image / `col-lg-9`
 *  content, nested `col-lg-8` details / `col-lg-4` price, then `col-md-8` confirmation+date /
 *  `col-md-4` status). Whole card is the link (CMS wraps the entire block in one `<a>`) — no
 *  separate "View Details" button. The 4 `fa-check` detail lines (stay dates, city, room count,
 *  cancellation policy) are 2-per-row ONLY at `lg`+ (bare `col-lg-6`, i.e. full-width below lg,
 *  matching the reference) — `PolicyName` isn't in our list payload, so the existing
 *  `cancellationState`-derived Free/Non-refundable label fills that slot. Status is plain colored
 *  text (`HOTEL_STATUS_CLASS`), never a pill — the CMS `statusHTML` is a bare `<h5><span>`. Status
 *  is tab-driven like `ShowsBookingCard`: the Other tab's query mixes genuine cancellations with
 *  merely-past-checkout "Completed"/"No show" bookings (see `booking-cart.repository.ts`), so only
 *  a real "Cancelled" is worth calling out there — "Completed"/"No show" show no label at all. */
function HotelBookingCard({ booking, activeTab }: { booking: BookingListItem; activeTab: 'active' | 'other' }) {
  // Cancellation label mirrors CMS `Rates[0].PolicyName` (detail-page-data.js:2567/3236): ONLY
  // "Free Cancellation" or "Non-refundable". The chargeable state shows NOTHING (CMS sets PolicyName
  // to '' and the list gates on a truthy value) — do not show "Cancellation charges apply" or a charge
  // amount here. >1 policy type → still nothing (CMS shows only the first rate's PolicyName).
  const policyLabel =
    booking.cancellationState === 'free'
      ? 'Free Cancellation'
      : booking.cancellationState === 'nonRefundable'
        ? 'Non-refundable'
        : '';
  const roomsLabel = booking.totalRooms
    ? `${booking.totalRooms} Room${booking.totalRooms !== 1 ? 's' : ''}`
    : '';
  const detailItems = [booking.formattedDate, booking.propertyCity, roomsLabel, policyLabel].filter(
    (v): v is string => Boolean(v),
  );
  const showStatus =
    activeTab === 'active' ||
    booking.statusLabel === 'Cancelled' ||
    booking.statusLabel === 'No show';
  return (
    <Link href={`${PROXY_PREFIX}/manage-bookings/booking-confirmed?id=${booking.id}`} className="block no-underline">
      <div className="p-6 rounded-lg mb-6 bg-white rebbet-shape">
        <div className="flex flex-col lg:flex-row -mx-[15px]">
          <div className="lg:w-1/4 px-[15px]">
            {booking.propertyPrimaryImage && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={booking.propertyPrimaryImage}
                alt={booking.propertyName}
                className="w-full rounded-lg mb-2 lg:mb-0"
              />
            )}
          </div>
          <div className="lg:w-3/4 px-[15px]">
            <div className="flex flex-col lg:flex-row -mx-[15px]">
              <div className="lg:w-2/3 px-[15px]">
                <h4 className="font-display font-bold text-secondary leading-snug">
                  {booking.propertyName}
                </h4>
                <div className="flex flex-wrap pb-3">
                  {detailItems.map((label, i) => (
                    <div key={i} className="lg:w-1/2 pt-2 text-black">
                      <i className="fa-solid fa-check pr-2" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:w-1/3 px-[15px]">
                {booking.showPrice != null && (
                  <div className="lg:text-right">
                    <span className="text-[1.5rem] font-bold text-black inline-flex items-center gap-1.5">
                      {booking.currencySymbol} {booking.showPrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col md:flex-row -mx-[15px]">
              <div className="md:w-2/3 px-[15px] text-black">
                {booking.userReservationId && (
                  <div>
                    <span className="text-[#0065A1]">Booking Confirmation Number: </span>
                    <span>{booking.userReservationId}</span>
                  </div>
                )}
                {booking.bookingDate && (
                  <div className="mt-2">
                    <span className="text-[#0065A1]">Booking Date:</span> {booking.bookingDate}
                  </div>
                )}
              </div>
              <div className="md:w-1/3 px-[15px] md:text-right">
                {/* Other tab shows a status label for a genuine "Cancelled" AND for "No show"
                    (BCP-2046) — CMS parity: `manage-booking-api.js:282-292` maps BOTH
                    GUEST_NO_SHOW and RESERVATION_CANCELLED to a red label on its Past tab.
                    "Completed" (past-checkout, still-Confirmed) also surfaces inside the Other
                    Bookings query (`checkoutDateISO < today`) and stays silent — the CMS blanks
                    that one too. */}
                {showStatus && booking.statusLabel && (
                  <h5>
                    <span className={HOTEL_STATUS_CLASS[booking.statusLabel] || ''}>
                      {booking.statusLabel}
                    </span>
                  </h5>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

/** 1:1 port of `dashboard-code/manage-booking.html`'s restaurant card (`col-md-2` image /
 *  `col-md-8` details / `col-md-2` status — no price, the reference's `.price` div is always
 *  empty for restaurants). Whole card is the link, no separate "View Details" button, status is
 *  plain colored text (Cancelled → red, everything else → green — matches the reference's binary
 *  `bookingStatus == "Cancelled" ? danger : green` exactly, no other states are distinguished).
 *  Tab-gated like the other cards: the Other tab only calls out a genuine "Cancelled" — any other
 *  status (e.g. "Pending") shows no label there. */
function RestaurantBookingCard({
  booking,
  activeTab,
}: {
  booking: RestaurantBookingListItem;
  activeTab: 'active' | 'other';
}) {
  const bookingDate = formatRestaurantBookingDate(booking.CreatedDate);
  const showStatus = activeTab === 'active' || booking.bookingStatus === 'Cancelled';
  // No Restaurant booking-detail page exists yet — this link is a known, deferred gap (see
  // amit-local-work/bip-vs-cms-deviations.md). Out of scope for the Shows/Sports memberId +
  // routing fix; left pointing at the not-yet-built sub-route rather than removing the link.
  return (
    <Link
      href={`${PROXY_PREFIX}/manage-bookings/restaurant-booking-detail?cartId=${booking._id}`}
      className="block no-underline"
    >
      <div className="p-6 rounded-lg mb-6 bg-white rebbet-shape">
        <div className="flex flex-col md:flex-row -mx-[15px]">
          <div className="md:w-1/6 px-[15px]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={resolveRestaurantImage(booking.Image)}
              alt={booking.Name || 'Restaurant'}
              className="w-full rounded-lg object-contain bg-gray-100 min-h-[100px]"
              onError={(e) => {
                const el = e.currentTarget;
                if (el.src !== RESTAURANT_NO_IMAGE_URL) {
                  el.onerror = null;
                  el.src = RESTAURANT_NO_IMAGE_URL;
                }
              }}
            />
          </div>
          <div className="md:w-4/6 px-[15px]">
            <h4 className="font-display font-bold text-secondary leading-snug">{booking.Name}</h4>
            {booking.VenueDetail && (
              <div className="text-[15px] text-black mb-2 md:mb-4">{booking.VenueDetail}</div>
            )}
            <div className="grid grid-cols-3 gap-x-[30px] pt-1">
              {booking.BookingDateTime && (
                <div>
                  <div className="text-[15px] text-[#0065A1] mb-[2px]">Slot Date &amp; Time</div>
                  <div className="text-[15px] text-black">{booking.BookingDateTime}</div>
                </div>
              )}
              {booking.GuestDisplay && (
                <div>
                  <div className="text-[15px] text-[#0065A1] mb-[2px]">Number of Guests</div>
                  <div className="text-[15px] text-black">{booking.GuestDisplay}</div>
                </div>
              )}
              {booking.TableName && (
                <div>
                  <div className="text-[15px] text-[#0065A1] mb-[2px]">Table Number</div>
                  <div className="text-[15px] text-black">{booking.TableName}</div>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-1">
              {booking.reservationId && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Confirmation Number: </span>
                  <span className="text-black">{booking.reservationId}</span>
                </div>
              )}
              {bookingDate && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Date: </span>
                  <span className="text-black">{bookingDate}</span>
                </div>
              )}
            </div>
          </div>
          <div className="md:w-1/6 px-[15px] md:text-right mt-4 md:mt-0 flex flex-col justify-between">
            <div />
            {showStatus && booking.bookingStatus && (
              <h5>
                <span className={booking.bookingStatus === 'Cancelled' ? 'text-danger' : 'text-green'}>
                  {booking.bookingStatus}
                </span>
              </h5>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}


/** Format booking date as "DD MMMM YYYY", e.g. "26 August 2026". */
function formatOrderDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

/** Exact port of `retDatObj.TotalPrice`:
 *  `data[x].displayCurrencySymbol + ' ' + data[x].displayTotalAmount.toFixed(2)` — note the space
 *  between symbol and amount, and the fixed 2 decimals (our prior port had neither). */
function totalPriceDisplay(booking: SportsBookingListItem): string {
  if (booking.displayTotalAmount == null) return '';
  return `${booking.displayCurrencySymbol ?? ''} ${booking.displayTotalAmount.toFixed(2)}`;
}

/** Complete venue address for Sports & Concerts bookings without repeating city/country */
function venueDetail(booking: SportsBookingListItem): string {
  const venue = booking.eventDetail?.venue;
  const venueCountry = typeof venue?.country === 'object' ? venue?.country?.name : venue?.country;
  const venueCity = typeof venue?.city === 'object' ? venue?.city?.name : venue?.city;
  const eventCountry = typeof booking.eventDetail?.country === 'object' ? booking.eventDetail?.country?.name : booking.eventDetail?.country;
  const eventCity = typeof booking.eventDetail?.city === 'object' ? booking.eventDetail?.city?.name : booking.eventDetail?.city;

  const rawParts = [
    venue?.name,
    venue?.address,
    venueCity || eventCity,
    venueCountry || eventCountry,
  ];

  const result: string[] = [];
  const seen = new Set<string>();

  for (const raw of rawParts) {
    if (!raw) continue;
    const str = String(raw).trim();
    if (!str) continue;

    const currentJoined = result.join(', ').toLowerCase();
    if (currentJoined && currentJoined.includes(str.toLowerCase())) {
      continue;
    }

    const segments = str.split(',').map((s) => s.trim()).filter(Boolean);
    for (const seg of segments) {
      const key = seg.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(seg);
      }
    }
  }

  if (!result.length) return 'Location Unavailable';
  return result.join(', ');
}

function formatPerformanceDate(iso?: string): string {
  if (!iso) return '';
  return formatEventDateTime(iso);
}

function sportsTicketAndCategoryDisplay(booking: SportsBookingListItem): string {
  const total = booking.tickets?.reduce((sum, t) => sum + (t.quantity || 0), 0) ?? 0;
  const countStr = total === 1 ? '1 Ticket' : `${total} Ticket(s)`;
  const t0 = booking.ticketDetail?.[0] || booking.tickets?.[0];
  if (!t0) return countStr;
  const sitting = t0.sittingArrangements || '';
  const rawCat = t0.categoryName || '';
  const cat = cleanCategoryName(rawCat);
  // Ticket perks (VIP package inclusions, e.g. "Meet & Greet ▪ Free Parking") — same field the
  // ticket-listing card (`TicketCard.splittedCategoryName.packageDetails`) shows, snapshotted onto
  // the booking's `ticketDetail` at booking-create time.
  const perks = t0.splittedCategoryName?.packageDetails || '';
  const catWithPerks =
    perks && !cat.toLowerCase().includes(perks.toLowerCase()) ? `${cat} (${perks})` : cat;
  const catPart = [sitting, catWithPerks].filter(Boolean).join(' - ');
  return [countStr, catPart].filter(Boolean).join(' - ');
}

/** 1:1 port of `dashboard-code/manage-booking.html`'s sports card (`.manage-booking-card` +
 *  `row align-items-top` / `col-md-8` + `col-md-4`). Tab-gated like the other cards: `status`
 *  can be "Pending"/"Confirmed"/"Cancelled" (`sportsEvents365OrderStatus`) — the Other tab only
 *  calls out a genuine "Cancelled", never "Pending". */
function SportsBookingCard({
  booking,
  activeTab,
}: {
  booking: SportsBookingListItem;
  activeTab: 'active' | 'other';
}) {
  const ticketAndCategory = sportsTicketAndCategoryDisplay(booking);
  // `status` (our own field, set to 'Cancelled' by `cancelBooking`) is checked FIRST — it's the
  // authoritative, always-current field. `sportsEvents365OrderStatus` (the vendor's own order
  // status) is a one-time SNAPSHOT that's NEVER updated when a booking is cancelled on our side,
  // so a stale snapshot (e.g. still "Pending") must not be able to mask a real cancellation. Only
  // fall back to it when `status` itself is unset.
  const displayStatus = booking.status || booking.sportsEvents365OrderStatus;
  const showStatus = activeTab === 'active' || displayStatus === 'Cancelled';
  const bookingDate = formatOrderDate(booking.CreatedDate);
  const performanceDate = formatPerformanceDate(booking.performanceDate);
  const venue = venueDetail(booking);
  const userReservationId = booking.sportsEvents365OrderId || booking.reservationId;
  return (
    <Link
      href={`${PROXY_PREFIX}/manage-bookings/sports-booking-detail?cartId=${booking.id}`}
      className="block no-underline"
    >
      <div className="p-6 rounded-lg mb-6 bg-white rebbet-shape">
        <div className="flex flex-col md:flex-row md:items-stretch -mx-[15px]">
          <div className="md:w-2/3 px-[15px]">
            <h4 className="font-display font-bold text-secondary leading-snug">
              {booking.eventName || ''}
            </h4>
            {venue && (
              <div className="text-[15px] text-black mb-2 md:mb-3">{venue}</div>
            )}
            <div className="pt-1 flex flex-wrap -mx-[15px]">
              {performanceDate && (
                <div className="w-1/2 px-[15px]">
                  <div className="text-[15px] text-[#0065A1] mb-[2px] block">Sport Date &amp; Time</div>
                  <div className="text-[15px] text-black">{performanceDate}</div>
                </div>
              )}
              {ticketAndCategory && (
                <div className="w-1/2 px-[15px]">
                  <div className="text-[15px] text-[#0065A1] mb-[2px] block">Number of Tickets</div>
                  <div className="text-[15px] text-black">{ticketAndCategory}</div>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-1">
              {userReservationId && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Confirmation Number: </span>
                  <span className="text-black">{userReservationId}</span>
                </div>
              )}
              {bookingDate && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Date: </span>
                  <span className="text-black">{bookingDate}</span>
                </div>
              )}
            </div>
          </div>
          <div className="md:w-1/3 px-[15px] md:text-right mt-4 md:mt-0 flex flex-col justify-between">
            <div className="text-[18px] md:text-[1.5rem] font-semibold text-black max-md:mt-[10px]">
              {totalPriceDisplay(booking)}
            </div>
            {showStatus && (
              <h5 className="text-[1.25rem]">
                <span className={displayStatus === 'Cancelled' ? 'text-danger' : 'text-green'}>
                  {displayStatus}
                </span>
              </h5>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function concertTicketAndCategoryDisplay(booking: SportsBookingListItem): string {
  const total = booking.tickets?.reduce((sum, t) => sum + (t.quantity || 0), 0) ?? 0;
  const countStr = total === 1 ? '1 Ticket' : `${total} Ticket(s)`;
  const tickets = booking.tickets;
  if (!tickets?.length) return countStr;
  const ticketType = tickets[0]?.sittingArrangements || '';
  const ticketDetails = booking.ticketDetail;

  const seen = new Set<string>();
  const categories: string[] = [];
  for (const t of tickets) {
    const detail = ticketDetails?.find((d) => d?.id === t.ticketId || d?.ticketId === t.ticketId);
    const raw =
      detail?.categoryName ||
      detail?.splittedCategoryName?.secondary ||
      detail?.splittedCategoryName?.primary ||
      t.categoryName ||
      '';
    // Same field the ticket-listing card (`TicketCard.splittedCategoryName.packageDetails`) shows —
    // snapshotted onto the booking's `ticketDetail` at booking-create time.
    const perks = detail?.splittedCategoryName?.packageDetails || '';

    let label = raw.trim();
    if (perks && !label.toLowerCase().includes(perks.toLowerCase())) {
      label = `${label} (${perks})`;
    }

    const key = label.toLowerCase();
    if (label && !seen.has(key)) {
      seen.add(key);
      categories.push(label);
    }
  }

  const categoryPart = categories.join(' , ');
  return [countStr, ticketType, categoryPart].filter(Boolean).join(' - ');
}

/** Concerts card — matching CMS sand-bookitplease reference. Tab-gated like `SportsBookingCard`:
 *  the Other tab only calls out a genuine "Cancelled", never "Pending". */
function ConcertsBookingCard({
  booking,
  activeTab,
}: {
  booking: SportsBookingListItem;
  activeTab: 'active' | 'other';
}) {
  const bookingDate = formatOrderDate(booking.CreatedDate);
  const performanceDate = formatPerformanceDate(booking.performanceDate);
  const ticketAndCategory = concertTicketAndCategoryDisplay(booking);
  const venue = venueDetail(booking);
  // `status` (our own field, set to 'Cancelled' by `cancelBooking`) is checked FIRST — it's the
  // authoritative, always-current field. `sportsEvents365OrderStatus` (the vendor's own order
  // status) is a one-time SNAPSHOT that's NEVER updated when a booking is cancelled on our side,
  // so a stale snapshot (e.g. still "Pending") must not be able to mask a real cancellation. Only
  // fall back to it when `status` itself is unset.
  const displayStatus = booking.status || booking.sportsEvents365OrderStatus;
  const showStatus = activeTab === 'active' || displayStatus === 'Cancelled';
  const userReservationId = booking.sportsEvents365OrderId || booking.reservationId;
  return (
    <Link
      href={`${PROXY_PREFIX}/manage-bookings/concert-booking-detail?cartId=${booking.id}`}
      className="block no-underline"
    >
      <div className="p-6 rounded-lg mb-6 bg-white rebbet-shape">
        <div className="flex flex-col md:flex-row md:items-stretch -mx-[15px]">
          <div className="md:w-2/3 px-[15px]">
            <h4 className="font-display font-bold text-secondary leading-snug">
              {booking.eventName || ''}
            </h4>
            {venue && (
              <div className="text-[15px] text-black mb-2 md:mb-3">{venue}</div>
            )}
            <div className="pt-1 flex flex-wrap -mx-[15px]">
              {performanceDate && (
                <div className="w-1/2 px-[15px]">
                  <div className="text-[15px] text-[#0065A1] mb-[2px] block">Concert Date &amp; Time</div>
                  <div className="text-[15px] text-black">{performanceDate}</div>
                </div>
              )}
              {ticketAndCategory && (
                <div className="w-1/2 px-[15px]">
                  <div className="text-[15px] text-[#0065A1] mb-[2px] block">Number of Tickets</div>
                  <div className="text-[15px] text-black">{ticketAndCategory}</div>
                </div>
              )}
            </div>
            <div className="mt-4 space-y-1">
              {userReservationId && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Confirmation Number: </span>
                  <span className="text-black">{userReservationId}</span>
                </div>
              )}
              {bookingDate && (
                <div className="text-[15px]">
                  <span className="text-[#0065A1]">Booking Date: </span>
                  <span className="text-black">{bookingDate}</span>
                </div>
              )}
            </div>
          </div>
          <div className="md:w-1/3 px-[15px] md:text-right mt-4 md:mt-0 flex flex-col justify-between">
            <div className="text-[18px] md:text-[1.5rem] font-semibold text-black max-md:mt-[10px]">
              {totalPriceDisplay(booking)}
            </div>
            {showStatus && (
              <h5 className="text-[1.25rem]">
                <span className={displayStatus === 'Cancelled' ? 'text-danger' : 'text-green'}>
                  {displayStatus}
                </span>
              </h5>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

/** Loading placeholder for the initial fetch — same `SkeletonBox` primitive (globals.css
 *  `skeletonPulse`) every other page's skeleton uses (`_components/Skeleton.tsx`), composed into
 *  the SAME `rebbet-shape` white-card row every real booking card uses (image left / title+detail
 *  lines middle / price top-right, confirmation+date bottom-left / status bottom-right — see
 *  `HotelBookingCard` above), so there's no layout shift once the real cards fade in. Replaces the
 *  bare `null` this page used to render during `loading && items.length === 0`. */
function BookingCardSkeleton() {
  return (
    <div className="p-6 rounded-lg mb-6 bg-white rebbet-shape" aria-hidden="true">
      <div className="flex flex-col lg:flex-row -mx-[15px]">
        <div className="lg:w-1/4 px-[15px]">
          <SkeletonBox height={140} radius={8} className="mb-2 lg:mb-0" />
        </div>
        <div className="lg:w-3/4 px-[15px]">
          <div className="flex flex-col lg:flex-row -mx-[15px]">
            <div className="lg:w-2/3 px-[15px]">
              <SkeletonBox width="60%" height={22} mb={12} />
              <div className="flex flex-wrap gap-x-6 gap-y-2 pb-3">
                <SkeletonBox width={140} height={14} />
                <SkeletonBox width={110} height={14} />
                <SkeletonBox width={130} height={14} />
              </div>
            </div>
            <div className="lg:w-1/3 px-[15px]">
              <SkeletonBox width="50%" height={26} mb={6} className="lg:ml-auto" />
            </div>
          </div>
          <div className="flex flex-col md:flex-row -mx-[15px]">
            <div className="md:w-2/3 px-[15px]">
              <SkeletonBox width={220} height={14} mb={10} />
              <SkeletonBox width={160} height={14} />
            </div>
            <div className="md:w-1/3 px-[15px] md:flex md:justify-end">
              <SkeletonBox width={90} height={14} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingCardDispatch({
  booking,
  activeTab,
}: {
  booking: AnyBookingItem;
  activeTab: 'active' | 'other';
}) {
  if (booking.bookingType === 'restaurant') return <RestaurantBookingCard booking={booking} activeTab={activeTab} />;
  if (booking.bookingType === 'sports') return <SportsBookingCard booking={booking} activeTab={activeTab} />;
  if (booking.bookingType === 'concerts') return <ConcertsBookingCard booking={booking} activeTab={activeTab} />;
  if (booking.bookingType === 'shows') return <ShowsBookingCard booking={booking} activeTab={activeTab} />;
  if (booking.bookingType === 'hotel') return <HotelBookingCard booking={booking} activeTab={activeTab} />;
  return null;
}

export default function ManageBookingsPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [auth, setAuth] = useState<'loading' | 'in' | 'out'>('loading');
  const [memberId, setMemberId] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'other'>('active');
  // Seed from the URL so a confirmation page's "View my Bookings" link (?bookingType=N) renders on
  // its own tab with no flash of "All Bookings". Lazy initializer = first render only; from there
  // the effect below keeps this in lock-step with the URL in BOTH directions.
  const [category, setCategory] = useState(
    () => resolveCategoryFromBookingType(searchParams.get('bookingType')),
  );

  // URL → state, UNCONDITIONALLY (the old `if (bType)` / `if (resolved !== 'All Bookings')` guards
  // meant nothing could ever set the category back to 'All Bookings' — half of BCP-2050).
  // Keyed on the VALUE, not the `searchParams` object: `handleCategoryChange` writes the query with
  // a raw history write that the router never sees, so this dep stays put and cannot spontaneously
  // clobber the user's filter on an unrelated re-render. A REAL navigation (deep link, back button)
  // does change it, and then this syncs correctly. Do NOT add `category` to the deps — that loops.
  const bookingTypeParam = searchParams.get('bookingType');
  useEffect(() => {
    setCategory(resolveCategoryFromBookingType(bookingTypeParam));
  }, [bookingTypeParam]);

  // Keep the URL's ?bookingType=N in sync with the user's own dropdown choice (deep-linkable/
  // bookmarkable at any point, not just on the initial confirmation-page redirect). A RAW history
  // write, matching HotelsFilters/HotelsList: `router.replace` here triggers a real Next navigation
  // and visibly re-renders the whole page on every filter change, which is not worth a tidy URL.
  // The breadcrumb that this used to break is handled directly by `clearCategoryFilter` below.
  function writeCategoryToUrl(next: string) {
    const params = new URLSearchParams(window.location.search);
    if (next === 'All Bookings') {
      params.delete('bookingType');
    } else {
      params.set('bookingType', CATEGORY_TO_BOOKING_TYPE[next]);
    }
    const query = params.toString();
    window.history.replaceState(null, '', query ? `?${query}` : window.location.pathname);
  }

  function handleCategoryChange(next: string) {
    setCategory(next);
    writeCategoryToUrl(next);
  }

  // The "My Bookings" breadcrumb points at the page the user is already on, so Next treats the
  // click as a no-op navigation and the filter never cleared (BCP-2050 — it took two clicks).
  // Handle it here instead: reset the category and drop the query in place, no navigation at all.
  function clearCategoryFilter(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setCategory('All Bookings');
    writeCategoryToUrl('All Bookings');
  }
  const [items, setItems] = useState<AnyBookingItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  // Infinite scroll (CMS `manage-booking.js` `data-infinite-scroll="true"` — the reference
  // auto-loads the next page on scroll rather than a "Load more" button). Same sentinel +
  // IntersectionObserver pattern as the hotels listing page (`HotelsList.tsx`): refs mirror the
  // latest state each render so the observer's callback (created once) never reads a stale
  // closure over `page`/`totalPages`/`loading`.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef(page);
  const totalPagesRef = useRef(totalPages);
  const loadingRef = useRef(loading);
  // Full, already-globally-sorted merged list for "All Bookings" — populated once per fresh load
  // (fetches everything from all 3 sources), then every subsequent "page" just slices further into
  // this cached array instead of re-fetching. See ALL_BOOKINGS_FETCH_SIZE doc comment above.
  const allBookingsCacheRef = useRef<AnyBookingItem[]>([]);
  // Request-ordering guard — `fetchPage` has no `AbortController`/cancellation, so without this a
  // slow, earlier-started request (e.g. a Restaurants fetch still in flight when the category
  // switches to Shows, or the background refresh below firing mid-request) can resolve AFTER a
  // newer one and overwrite the screen with the WRONG category's data — the dropdown reads one
  // category while the cards on screen are from a stale, already-superseded fetch. Every call
  // increments this and captures its own id; a response only applies (`setItems`/`setTotalPages`)
  // when its id is still the latest one issued, so a late-arriving stale response is a silent no-op.
  const requestIdRef = useRef(0);
  useEffect(() => {
    pageRef.current = page;
    totalPagesRef.current = totalPages;
    loadingRef.current = loading;
  }, [page, totalPages, loading]);

  useEffect(() => {
    let cancelled = false;
    authClient
      .getMe()
      .then((me) => {
        if (cancelled) return;
        if (me.message !== 'success' || !me.data?._id) {
          setAuth('out'); // CMS parity: pop the login modal (LoginGate) instead of bouncing home.
        } else {
          setMemberId(me.data._id);
          setAuth('in');
        }
      })
      .catch(() => {
        if (!cancelled) setAuth('out');
      });
    return () => {
      cancelled = true;
    };
  }, [router]);

  const fetchPage = useCallback(
    async (pageNum: number, replace: boolean) => {
      // See `requestIdRef` doc comment above — captured up-front so every branch below can check
      // it's still the most-recently-issued call before touching `items`/`totalPages`.
      const myRequestId = ++requestIdRef.current;
      const isStale = () => requestIdRef.current !== myRequestId;
      if (!memberId) {
        if (isStale()) return;
        setItems([]);
        setTotalPages(0);
        return;
      }
      setLoading(true);
      try {
        // "All Bookings" defaults to the Hotels feed for now — merging all three
        // sources into one paginated view is wired separately (plan Section 4/5
        // step 6, the flagged cross-source-pagination follow-up).
        if (category === 'Restaurants') {
          // memberId is resolved server-side from the session cookie — not sent from the client.
          const res = await restaurantsClient.getBookings({
            status: activeTab === 'active' ? 'Confirmed' : 'Cancelled',
            page: pageNum,
            pagesize: PAGE_SIZE,
          });
          if (isStale()) return;
          setTotalPages(res.totalPages);
          setItems((prev) => (replace ? res.records : [...prev, ...res.records]));
        } else if (category === 'Shows') {
          // Native (2026-08-06; moved to shows-service 2026-08-26) — calls shows-service's own
          // Shows module directly, no CMS dependency; see
          // `services/shows-service/src/modules/shows/controllers/my-bookings.controller.ts`.
          const res = await showsClient.getMyTheaterBookings({
            memberId,
            status: activeTab,
            page: pageNum,
            pagesize: PAGE_SIZE,
          });
          if (isStale()) return;
          // The order docs don't carry a `bookingType` discriminator — CardFor()/itemId()/
          // dateKey() below all key off it, so stamp it on the way in (same as every other source).
          const records: ShowsBookingListItem[] = (res.data?.Records ?? []).map((r: ShowBooking) => ({
            ...r,
            bookingType: 'shows' as const,
          }));
          setTotalPages(res.data?.totalPages || 1);
          setItems((prev) => (replace ? records : [...prev, ...records]));
        } else if (category === 'Sports') {
          // memberId is resolved server-side from the session cookie (MemberSessionService) —
          // not sent from the client.
          const res = await sportsEventsClient.getBookings({
            status: activeTab,
            eventType: 'sport',
            page: pageNum,
            pagesize: PAGE_SIZE,
          });
          if (isStale()) return;
          setTotalPages(res.totalPages);
          setItems((prev) => (replace ? res.records : [...prev, ...res.records]));
        } else if (category === 'Concerts') {
          // concerts-service's own `/bookings/list` — same shape as sports (see
          // `concertsEventsClient`'s doc comment in `@bip/api-client`), no `eventType` filter
          // needed since every row in its `Concert_Orders` collection is already a concert.
          // memberId is resolved server-side from the session cookie — not sent from the client.
          const res = await concertsEventsClient.getBookings({
            status: activeTab,
            page: pageNum,
            pagesize: PAGE_SIZE,
          });
          if (isStale()) return;
          setTotalPages(res.totalPages);
          setItems((prev) => (replace ? res.records : [...prev, ...res.records]));
        } else if (category === 'Hotels') {
          // memberId is resolved server-side from the session cookie — not sent from the client.
          const res = await hotelsClient.getBookings({
            status: activeTab === 'active' ? 'Confirmed' : 'Cancelled',
            page: pageNum,
            pagesize: PAGE_SIZE,
          });
          if (isStale()) return;
          // Hotels records don't carry a `bookingType` discriminator — stamp it on the way in,
          // same as the Shows category branch below.
          const hotelRecords: HotelBookingItem[] = res.records.map((r) => ({ ...r, bookingType: 'hotel' as const }));
          setTotalPages(res.totalPages);
          setItems((prev) => (replace ? hotelRecords : [...prev, ...hotelRecords]));
        } else if (replace) {
          // "All Bookings", fresh load (tab/category switch) — fetch EVERYTHING from all 5 sources
          // in one shot (CMS parity, see ALL_BOOKINGS_FETCH_SIZE doc comment), merge, sort globally
          // by date ONCE, cache the full array, then slice out just the first page to display.
          const emptyPage = { records: [], totalPages: 0 };
          const [hotelsRes, restaurantsRes, sportsRes, concertsRes, showsRes] = await Promise.all([
            withTimeout(
              // memberId is resolved server-side from the session cookie — not sent from the client.
              hotelsClient
                .getBookings({
                  status: activeTab === 'active' ? 'Confirmed' : 'Cancelled',
                  page: 1,
                  pagesize: ALL_BOOKINGS_FETCH_SIZE,
                })
                .catch(() => emptyPage),
              MERGE_SOURCE_TIMEOUT_MS,
              emptyPage,
            ),
            withTimeout(
              // memberId is resolved server-side from the session cookie — not sent from the client.
              restaurantsClient
                .getBookings({ status: activeTab === 'active' ? 'Confirmed' : 'Cancelled', page: 1, pagesize: ALL_BOOKINGS_FETCH_SIZE })
                .catch(() => emptyPage),
              MERGE_SOURCE_TIMEOUT_MS,
              emptyPage,
            ),
            withTimeout(
              // memberId is resolved server-side from the session cookie — not sent from the client.
              sportsEventsClient
                .getBookings({ status: activeTab, page: 1, pagesize: ALL_BOOKINGS_FETCH_SIZE })
                .catch(() => emptyPage),
              MERGE_SOURCE_TIMEOUT_MS,
              emptyPage,
            ),
            withTimeout(
              // memberId is resolved server-side from the session cookie — not sent from the client.
              concertsEventsClient
                .getBookings({ status: activeTab, page: 1, pagesize: ALL_BOOKINGS_FETCH_SIZE })
                .catch(() => emptyPage),
              MERGE_SOURCE_TIMEOUT_MS,
              emptyPage,
            ),
            withTimeout(
              showsClient
                .getMyTheaterBookings({ memberId, status: activeTab, page: 1, pagesize: ALL_BOOKINGS_FETCH_SIZE })
                .then((res) => ({
                  records: res.data?.Records ?? [],
                  totalPages: res.data?.totalPages || 0,
                }))
                .catch(() => emptyPage),
              MERGE_SOURCE_TIMEOUT_MS,
              emptyPage,
            ),
          ]);
          if (isStale()) return;
          // Neither Hotels nor Shows order docs carry a `bookingType` discriminator — stamp both on
          // the way in, same as the direct "Hotels"/"Shows" category branches above.
          const hotelsRecords: HotelBookingItem[] = hotelsRes.records.map((r) => ({ ...r, bookingType: 'hotel' as const }));
          const showsRecords: ShowsBookingListItem[] = showsRes.records.map((r: ShowBooking) => ({
            ...r,
            bookingType: 'shows' as const,
          }));
          const merged = [
            ...hotelsRecords,
            ...restaurantsRes.records,
            ...sportsRes.records,
            ...concertsRes.records,
            ...showsRecords,
          ].sort((a, b) => dateKey(b) - dateKey(a));
          allBookingsCacheRef.current = merged;
          setTotalPages(Math.ceil(merged.length / PAGE_SIZE) || 0);
          setItems(merged.slice(0, PAGE_SIZE));
        } else {
          // "All Bookings", scroll-triggered "next page" — no network call at all, just slice
          // further into the already-fetched-and-sorted full list.
          const start = (pageNum - 1) * PAGE_SIZE;
          setItems((prev) => [...prev, ...allBookingsCacheRef.current.slice(start, start + PAGE_SIZE)]);
        }
      } catch {
        if (replace && !isStale()) setItems([]);
      } finally {
        if (!isStale()) setLoading(false);
      }
    },
    [memberId, activeTab, category],
  );

  // (Re)load page 1 whenever auth/tab/category changes. Clearing `items` here (not just inside
  // `fetchPage`) is what actually makes the switch visible — `fetchPage` only replaces `items`
  // once its fetch resolves, so without this the PREVIOUS tab's cards stayed on screen, unchanged,
  // for the whole request; the key-remount fade had nothing to fade into since the content never
  // visibly changed until the swap.
  useEffect(() => {
    if (auth !== 'in') return;
    setPage(1);
    setItems([]);
    setTotalPages(0);
    fetchPage(1, true);
  }, [auth, fetchPage]);

  // Active/Other (and every per-source status split, e.g. hotels' checkout-date cutoff) is a pure
  // function of wall-clock time — a booking whose performance/slot date+time crosses "now" while
  // this tab sits open in the background needs to silently hop tabs on its own, not wait for the
  // user to manually re-switch. Re-fetch page 1 (a) when the tab/window regains focus/visibility
  // (covers "left it open overnight, came back") and (b) on a periodic timer as a fallback for a
  // long-idle-but-still-visible tab. `fetchPage(1, true)` replaces `items` only once the request
  // resolves (see its own doc comment) — no `setItems([])` first, so this never flashes the
  // skeleton loader the way the tab/category-switch effect above does. Gated on `pageRef.current
  // === 1` so a user who has infinite-scrolled into page 2+ never has that state silently
  // clobbered by a background refresh mid-scroll.
  useEffect(() => {
    if (auth !== 'in') return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (pageRef.current !== 1) return;
      fetchPage(1, true);
    };
    const interval = setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [auth, fetchPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          pageRef.current < totalPagesRef.current &&
          !loadingRef.current
        ) {
          const next = pageRef.current + 1;
          setPage(next);
          fetchPage(next, false);
        }
      },
      { threshold: 0.1 },
    );
    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [fetchPage]);

  if (auth === 'out') {
    return <LoginGate onHome={() => router.replace(HOME_HREF)} />;
  }

  // `shellLoading` covers just the auth-check window (before we know whether to render the real
  // header/category-select/tabs or bounce to LoginGate) — its own skeleton shapes (title bar,
  // label+select box, pill row) are distinct from `BookingCardSkeleton` below, so it's visually
  // clear which loading skeleton belongs to which real element once it resolves.
  const shellLoading = auth !== 'in';
  const initialLoading = shellLoading || (loading && items.length === 0);

  return (
    <AccountShell
      parent={{ label: 'My Bookings', href: `${PROXY_PREFIX}/manage-bookings`, onClick: clearCategoryFilter }}
      crumb={category}
    >
      <div className="space-y-5">
        {/* Header row + category filter — CMS `<h2 class="mb-0 font-weight-bold">Manage Bookings</h2>`
            (base.css h2 = 2rem; plain body font, NOT `font-display`) + `<h6>Booking Category</h6>`
            stacked ABOVE the select (not an inline "Booking Category:" label). */}
        {shellLoading ? (
          <div className="flex flex-wrap items-start justify-between gap-3" aria-hidden="true">
            <SkeletonBox width={260} height={38} />
            <div>
              <SkeletonBox width={140} height={16} mb={8} />
              <SkeletonBox width={220} height={44} radius={10} />
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            {/* Explicit inline style, not just `font-bold` — globals.css:394 has an UNLAYERED
                `h2 { font-weight: var(--font-medium) }` rule (not inside any `@layer`), which beats
                any `@layer utilities` class regardless of what's applied (the same "unlayered CSS
                beats @layer utilities" gotcha documented in apps/web/CLAUDE.md). An inline style is
                the only reliable way to force 700 here without touching the shared global rule. */}
            <h2
              className="font-primary text-[2rem] text-secondary mb-0"
              style={{ fontWeight: 700, fontFamily: "owners, sans-serif", fontSize: "2rem" }}
            >
              Manage Bookings
            </h2>
            <div className="max-md:w-full">
              <h6 className="font-primary text-base font-medium text-secondary mb-2" style={{ fontFamily: "owners, sans-serif", fontWeight: 500, fontSize: "1rem" }}>Booking Category</h6>
              {/* `appearance-none` is required — without it, most browsers render a <select> with its
                  own native OS chrome (box/border/background), which ignores every custom Tailwind
                  class below (border-radius/shadow/border-color/bg all silently no-op) and reads as
                  "unstyled". Removing native appearance also removes the OS's own dropdown arrow, so
                  a manual chevron icon is added back below (positioned relative to THIS wrapper, not
                  the outer one — that outer one also contains the <h6> label above).
                  CMS `Manage Bookings.html` parity: `col-md-3 col-12` — full width on mobile, 25% of
                  this row (title + dropdown, not the account sidebar) from md (768px) up. */}
              <div className="relative w-full md:flex-[0_0_25%] md:max-w-[25%]">
                <select
                  id="category"
                  value={category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="appearance-none w-full min-w-[200px] border border-[#DEE2E6] rounded-[10px] pr-10 text-black font-semibold text-[1rem] shadow-[0_1px_2px_rgba(0,0,0,0.05)] outline-none bg-white"
                  style={{
                    paddingTop: '.375rem',
                    paddingBottom: '.375rem',
                    paddingLeft: '.75rem',
                    // Right padding gets extra room beyond the requested `.75rem` — this select is
                    // `appearance-none` with a manual chevron overlay (not the browser's native
                    // arrow, which is why plain Bootstrap `.375rem .75rem` collides here), so the
                    // text needs to stop clear of the icon's own space.
                    paddingRight: '1.75rem',
                    height: 'calc(1.7em + .75rem + 9px)',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.color = '#495057';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.borderColor = '#80bdff';
                    e.currentTarget.style.outline = '0';
                    e.currentTarget.style.boxShadow = '0 0 0 .2rem rgba(0, 123, 255, .25)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.color = '';
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.outline = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
                <i
                  className="fa-solid fa-chevron-down text-info text-xs absolute top-1/2 -translate-y-1/2 pointer-events-none max-md:left-auto! max-md:right-3"
                  style={{ left: '11rem' }}
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tabs — 1:1 port of the reference's `<ul class="nav nav-pills mb-4 border-bottom">` /
            `<a class="nav-link ... fnt20">`: plain text tabs with a 2px underline on the active
            tab (`.nav-pills .nav-link.active` in globals.css:3731, ported from the CMS's own
            nav-pills override — NOT a filled pill button, and NOT this project's `rounded-pill`
            component). The bottom line under the whole bar is the reference's `border-bottom`
            utility (`#dee2e6`, not a theme token). `w-1/4` matches the reference's literal `w-25`
            on each `<li>` even though there are only 2 tabs (same in every tab section of the
            reference — intentional, not something to "fix" into 50/50). */}
        {shellLoading ? (
          <div className="flex gap-6 mb-6 pb-4 border-b border-[#dee2e6]" style={{ marginTop: '-18px' }} aria-hidden="true">
            <SkeletonBox width={130} height={20} />
            <SkeletonBox width={130} height={20} />
          </div>
        ) : (
          <ul className="flex flex-wrap list-none pl-0 mb-6 border-b border-[#dee2e6]" style={{ marginTop: '-18px' }}>
            {(['active', 'other'] as const).map((tab) => (
              <li key={tab} className="w-1/4 max-md:w-1/2 text-center">
                {/* Border lives on the inner <span>, NOT the <button> — this project's own
                    `@layer base { button { border: none } }` reset (apps/web/CLAUDE.md Tailwind
                    gotchas) silently zeroes any `border-*` utility applied directly to a <button>,
                    which is why the active underline wasn't rendering. */}
                <button type="button" onClick={() => setActiveTab(tab)} className="inline-block">
                  <span
                    className={`inline-block pt-4 pb-2 px-4 text-[1.125rem] font-bold max-md:px-2! max-md:text-[0.938rem] max-md:whitespace-nowrap ${activeTab === tab
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-text'
                      }`}
                  >
                    {tab === 'active' ? 'Active Bookings' : 'Other Bookings'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Cards — `key` remounts on tab/category switch so the fade-in re-triggers each time,
            matching the CMS reference's Bootstrap `.fade` cross-fade (see globals.css `fadeIn`).
            No spinner during the switch itself — the reference doesn't show one mid-fade either;
            the container just fades in once its (cleared, then re-fetched) content is ready. */}
        <div
          key={`${activeTab}-${category}`}
          className="space-y-4 animate-[fadeIn_.1s_ease-in-out]"
          aria-busy={initialLoading || loading}
        >
          {initialLoading ? (
            <>
              {[0, 1, 2, 3].map((i) => <BookingCardSkeleton key={i} />)}
            </>
          ) : items.length === 0 ? (
            <div className="bg-white rebbet-shape px-10 py-16 text-center text-gray-text">
              <svg
                className="mx-auto mb-3 w-36 h-36"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
                style={{ fill: 'var(--primary-hover)' }}
              >
                <path d="m27.28 25.86c2.31-2.64 3.72-6.08 3.72-9.86 0-8.27-6.73-15-15-15s-15 6.73-15 15 6.73 15 15 15c3.78 0 7.22-1.41 9.86-3.72l3.43 3.43c.2.19.45.29.71.29s.51-.1.71-.29c.39-.39.39-1.02 0-1.41zm-24.28-9.86c0-7.17 5.83-13 13-13s13 5.83 13 13-5.83 13-13 13-13-5.83-13-13zm7-3c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm12 0c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-.5 6.59c.22.5 0 1.1-.51 1.32-.13.06-.27.09-.4.09-.38 0-.75-.22-.92-.6-.64-1.46-2.09-2.4-3.67-2.4s-3.03.94-3.68 2.41c-.22.5-.81.74-1.32.51-.5-.22-.73-.81-.51-1.32.97-2.19 3.14-3.6 5.51-3.6s4.54 1.41 5.5 3.59z" />
              </svg>
              <h4 className="text-[0.938rem] font-medium text-black">No records found</h4>
            </div>
          ) : (
            items.map((b) => (
              <BookingCardDispatch
                key={`${b.bookingType}-${itemId(b)}`}
                booking={b}
                activeTab={activeTab}
              />
            ))
          )}
          {/* Infinite-scroll sentinel — CMS parity (data-infinite-scroll), no "Load more" button.
              Rendered UNCONDITIONALLY, in the same JSX position regardless of loading/empty/results
              state — matching HotelsList.tsx's own sentinel exactly (its is the LAST child, outside
              every conditional branch). Nesting it inside the "items.length > 0" branch (as before)
              meant the sentinel's DOM node didn't exist yet during the loading/empty states, and
              since the IntersectionObserver effect only runs once per `fetchPage` identity (not on
              every items/loading change), it could grab a stale/null ref depending on timing —
              confirmed via instrumented Playwright testing (the observer attached and fired once,
              but never again despite the target genuinely scrolling into view — an isolated minimal
              IntersectionObserver test in the same browser worked fine, ruling out an environment
              limitation and pointing at this exact conditional-mount mismatch). The observer
              callback still checks `page < totalPages` before loading more. */}
          {/* Load-more skeletons — same convention as HotelsList.tsx's own "load-more skeletons
              live inside the same grid" pattern: card-shaped placeholders instead of a bare
              spinner while the next page of an infinite-scroll fetch is in flight. */}
          {loading && items.length > 0 && page < totalPages && (
            <>
              {[0, 1, 2, 3].map((i) => <BookingCardSkeleton key={i} />)}
            </>
          )}
          <div ref={sentinelRef} className="text-center py-6" />
        </div>
      </div>
    </AccountShell>
  );
}
